/*
 * Client-side orchestrator for batch-uploading scraped image URLs into AEM DAM.
 * Chunks the input into batches of 5, invokes the `upload-to-dam` runtime
 * action per batch sequentially, and yields per-image results as they come in.
 *
 * The caller consumes it like:
 *
 *   for await (const item of uploadAssetsInBatches(urls, ctx)) {
 *     // item = { sourceUrl, ok, path?, error?, fileName? }
 *     setImages(prev => mergeResult(prev, item))
 *   }
 *
 * Keeping batches sequential (rather than firing all batches in parallel)
 * gives the user a staircase of UI updates instead of a single long wait,
 * and avoids slamming the AEM initiateUpload endpoint with many files at once.
 */

import actionWebInvoke from '../utils';

export const UPLOAD_BATCH_SIZE = 5;

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

/**
 * @param {Array<string|{url:string, fileName?:string}>} sourceItems - URLs or {url, fileName} objects
 * @param {object} ctx
 * @param {string} ctx.actionUrl        - the deployed upload-to-dam web action URL
 * @param {string} ctx.imsToken         - Bearer IMS access token
 * @param {string} ctx.authorUrl        - AEM Author URL (e.g. https://author-...adobeaemcloud.com)
 * @param {string} ctx.orgId            - IMS org id
 * @param {string} ctx.targetFolderPath - DAM folder to upload to (e.g. /content/dam/wknd-universal)
 * @yields {object} per-image result — { sourceUrl, ok, path?, error?, fileName? }
 */
export async function* uploadAssetsInBatches(sourceItems, ctx) {
  const { actionUrl, imsToken, authorUrl, orgId, targetFolderPath } = ctx || {};
  if (!actionUrl) throw new Error('uploadAssetsInBatches: missing actionUrl');
  if (!imsToken) throw new Error('uploadAssetsInBatches: missing imsToken');
  if (!authorUrl) throw new Error('uploadAssetsInBatches: missing authorUrl');
  if (!targetFolderPath) throw new Error('uploadAssetsInBatches: missing targetFolderPath');

  const normalized = sourceItems
    .filter(Boolean)
    .map((item) => typeof item === 'string' ? { url: item } : item)
    .filter((item) => item.url);
  const batches = chunk(normalized, UPLOAD_BATCH_SIZE);

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const hasMetadata = batch.some((it) => it.name);
    const headers = { Authorization: `Bearer ${imsToken}` };
    const body = {
      ...(hasMetadata
        ? { imageItems: batch }
        : { imageUrls: batch.map((it) => it.url) }),
      authorUrl,
      orgId,
      targetFolderPath,
      bearer: imsToken,
    };

    let resp;
    try {
      resp = await actionWebInvoke(actionUrl, headers, body, { method: 'POST' });
    } catch (err) {
      // Network-level failure for the whole batch — emit one error per item
      // so the caller can mark them as failed.
      const msg = (err && err.message) || String(err);
      for (const it of batch) yield { sourceUrl: it.url, ok: false, error: msg };
      continue;
    }

    const results =
      (resp && Array.isArray(resp.results) && resp.results) ||
      (resp && resp.body && Array.isArray(resp.body.results) && resp.body.results) ||
      null;

    if (!results) {
      const msg = resp?.error?.body?.error || resp?.error || 'upload-to-dam: invalid response';
      for (const it of batch) yield { sourceUrl: it.url, ok: false, error: typeof msg === 'string' ? msg : JSON.stringify(msg) };
      continue;
    }

    // Emit each result individually — caller can update UI per item.
    for (const r of results) yield r;
  }
}
